/**
 * useKeyboardNavigation Hook
 *
 * Handles keyboard shortcuts for timeline navigation:
 * - Arrow keys for panning
 * - +/- for zooming
 * - Home/End for reset
 */

import { useEffect, useCallback, type RefObject } from 'react'

interface UseKeyboardNavigationOptions {
  /** Container element ref for focus detection */
  containerRef: RefObject<HTMLElement>
  /** Callback for zoom in */
  onZoomIn: () => void
  /** Callback for zoom out */
  onZoomOut: () => void
  /** Callback for pan left */
  onPanLeft: () => void
  /** Callback for pan right */
  onPanRight: () => void
  /** Callback for reset */
  onReset: () => void
  /** Whether keyboard navigation is enabled. Default: true */
  enabled?: boolean
  /** Pan step size in pixels. Default: 50 */
  panStep?: number
}

/**
 * Attaches keyboard event handlers for timeline navigation
 */
export function useKeyboardNavigation({
  containerRef,
  onZoomIn,
  onZoomOut,
  onPanLeft,
  onPanRight,
  onReset,
  enabled = true,
  panStep: _panStep = 50,
}: UseKeyboardNavigationOptions): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      // Check if the container or a child has focus
      const container = containerRef.current
      if (!container) return

      // Only handle events if container or descendant is focused
      if (!container.contains(document.activeElement) && document.activeElement !== container) {
        return
      }

      // Don't handle if user is typing in an input
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          onPanRight() // Panning right moves content left, showing earlier dates
          break

        case 'ArrowRight':
          event.preventDefault()
          onPanLeft() // Panning left moves content right, showing later dates
          break

        case '+':
        case '=':
          event.preventDefault()
          onZoomIn()
          break

        case '-':
        case '_':
          event.preventDefault()
          onZoomOut()
          break

        case 'Home':
          event.preventDefault()
          onReset()
          break

        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            onReset()
          }
          break

        default:
          break
      }
    },
    [enabled, containerRef, onZoomIn, onZoomOut, onPanLeft, onPanRight, onReset]
  )

  useEffect(() => {
    if (!enabled) return

    const container = containerRef.current
    if (!container) return

    // Make container focusable if it isn't already
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '0')
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, containerRef, handleKeyDown])
}

/**
 * Get ARIA labels for keyboard navigation
 */
export function getKeyboardNavigationAriaLabel(): string {
  return 'Timeline. Use arrow keys to pan, plus/minus to zoom, Home to reset.'
}
